import { BackendConfig } from './config/BackendConfig';
import { BackendModule } from './backends/types';
import { IAuthService } from './services/auth/AuthService';
import { IDataClientService } from './services/data/DataClientService';
import { IStorageService } from './services/storage/StorageService';
import { IEventService } from './services/events/EventService';

/**
 * Service registry that manages all platform services
 * Uses singleton pattern to ensure consistent service instances across the application
 */
export class ServiceRegistry {
  private static instance: ServiceRegistry;
  private config: BackendConfig;
  private backend: BackendModule;

  // Service instances
  private _authService?: IAuthService;
  private _dataClientService?: IDataClientService;
  private _storageService?: IStorageService;
  private _eventService?: IEventService;
  
  private constructor(config: BackendConfig, backend: BackendModule) {
    this.config = config;
    this.backend = backend;
  }
  
  /**
   * Initialize the service registry with configuration
   */
  static initialize(config: BackendConfig, backend: BackendModule): ServiceRegistry {
    if (ServiceRegistry.instance) {
      throw new Error('ServiceRegistry is already initialized');
    }
    ServiceRegistry.instance = new ServiceRegistry(config, backend);
    return ServiceRegistry.instance;
  }
  
  /**
   * Get the singleton instance
   */
  static getInstance(): ServiceRegistry {
    if (!ServiceRegistry.instance) {
      throw new Error('ServiceRegistry is not initialized. Call initialize() first.');
    }
    return ServiceRegistry.instance;
  }

  initStorageService(tenantId: string) {
    this._storageService = this.createStorageService(tenantId);
  }
  /**
   * Get authentication service
   */
  getAuthService(): IAuthService {
    if (!this._authService) {
      this._authService = this.createAuthService();
    }
    return this._authService;
  }
  
  /**
   * Get data client service
   */
  getDataClientService(): IDataClientService {
    if (!this._dataClientService) {
      this._dataClientService = this.createDataClientService();
    }
    return this._dataClientService;
  }
  
  /**
   * Get storage service
   */
  getStorageService(): IStorageService {
    if (!this._storageService) {
      throw new Error('StorageService is not initialized');
    }
    return this._storageService;
  }
  
  /**
   * Get event service
   */
  getEventService(): IEventService {
    if (!this._eventService) {
      this._eventService = this.createEventService();
    }
    return this._eventService;
  }
  
  /**
   * Factory method for authentication service
   */
  private createAuthService(): IAuthService {
    return this.backend.createAuthService(this.config);
  }
  
  /**
   * Factory method for data client service
   */
  private createDataClientService(): IDataClientService {
    return this.backend.createDataClientService(this.config, this.getAuthService());
  }
  
  /**
   * Factory method for storage service
   */
  private createStorageService(tenantId: string): IStorageService {
    return this.backend.createStorageService(this.config, tenantId);
  }
  
  /**
   * Factory method for event service
   */
  private createEventService(): IEventService {
    return this.backend.createEventService(this.config);
  }
  
  /**
   * Reset the registry (useful for testing)
   */
  static reset(): void {
    ServiceRegistry.instance = undefined as any;
  }
}
